import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const ProductCard = ({ product, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
      },
    },
  };

  const expandVariants = {
    collapsed: { maxHeight: 0, opacity: 0 },
    expanded: {
      maxHeight: 200,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="bg-card rounded-lg border border-border overflow-hidden elevation-subtle hover:elevation-moderate transition-all duration-300 group"
      variants={cardVariants}
      whileHover={{ y: -2 }}
    >
      {/* Card Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name={product.icon} size={24} className="text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-card-foreground mb-1">
                {product.title}
              </h3>
              <p className="text-sm text-text-secondary font-medium">
                {product.subtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
              {t("comingSoon")}
            </span>
          </div>
        </div>

        <p className="text-text-secondary leading-relaxed">
          {product.description}
        </p>
      </div>

      {/* Preview Content */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-card-foreground uppercase tracking-wide">
            Key Features
          </h4>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-accent hover:text-accent/80 transition-colors"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </button>
        </div>

        {/* Feature Preview */}
        <div className="space-y-2 mb-4">
          {product.features?.slice(0, 2).map((feature, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
              <span className="text-sm text-text-secondary">{feature}</span>
            </div>
          ))}
        </div>

        {/* Expandable Features */}
        <motion.div
          initial="collapsed"
          animate={isExpanded ? "expanded" : "collapsed"}
          variants={expandVariants}
          className="overflow-hidden"
        >
          <div className="space-y-2 mb-4">
            {product.features?.slice(2).map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                <span className="text-sm text-text-secondary">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pipeline Visualization */}
        <div className="relative h-2 bg-muted rounded-full mb-6 overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-accent to-accent/60 rounded-full"
            animate={{
              x: ["0%", "100%", "0%"],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.5,
            }}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="outline"
            size="sm"
            iconName="Info"
            iconPosition="left"
            className="flex-1"
          >
            {t("learnMore")}
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Bell"
            iconPosition="left"
            className="flex-1 bg-accent hover:bg-accent/90"
          >
            {t("earlyAccess")}
          </Button>
        </div>
      </div>

      {/* Technical Specifications Preview */}
      <div className="px-6 pb-6">
        <div className="bg-muted rounded-lg p-4">
          <h5 className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
            Technical Preview
          </h5>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-text-secondary">Architecture:</span>
              <span className="text-card-foreground ml-2">Microservices</span>
            </div>
            <div>
              <span className="text-text-secondary">Deployment:</span>
              <span className="text-card-foreground ml-2">Cloud-native</span>
            </div>
            <div>
              <span className="text-text-secondary">API:</span>
              <span className="text-card-foreground ml-2">REST/GraphQL</span>
            </div>
            <div>
              <span className="text-text-secondary">Integration:</span>
              <span className="text-card-foreground ml-2">Enterprise</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
